import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import toast from 'react-hot-toast';
import { getProductById, getProductName, getProductShortName } from './productData';

/**
 * Sayıyı formatlar (ondalık ayırıcı olarak virgül kullanır)
 * @param {Number} number - Formatlanacak sayı
 * @param {Number} decimals - Ondalık basamak sayısı (varsayılan: 2)
 * @returns {String} - Formatlanmış sayı
 */
const formatNumber = (number, decimals = 2) => {
  if (number === null || number === undefined) return '-';
  return Number(number).toLocaleString('tr-TR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Hesaplama sonuçlarını Excel dosyasına aktarır
 * @param {Object} calculationResults - Hesaplama sonuçları veya toplu dışa aktarım için {bulkExport: true, calculations: [...]} nesnesi
 * @param {String} projectName - Proje adı
 * @param {String} roomName - Oda adı
 * @param {Function} t - Translation function for i18n support
 */
export const exportToExcel = async (calculationResults, projectName = 'Proje', roomName = 'Oda', t = null, language = 'tr') => {
  // Toplu dışa aktarım kontrolü
  if (calculationResults.bulkExport && Array.isArray(calculationResults.calculations)) {
    return exportBulkCalculations(calculationResults.calculations, t);
  }

  if (!calculationResults || !calculationResults.room) {
    console.error('Dışa aktarma için geçerli hesaplama sonuçları bulunamadı');
    return;
  }

  try {
    // Yeni bir çalışma kitabı oluştur
    const workbook = new ExcelJS.Workbook();
    
    // Çalışma kitabı özellikleri
    workbook.creator = 'Alas Fire Design Calculator';
    workbook.lastModifiedBy = 'Alas Fire';
    workbook.created = new Date();
    workbook.modified = new Date();
    
    // Çalışma sayfası ekle
    const worksheet = workbook.addWorksheet('Hesaplama Raporu', {
      pageSetup: {
        paperSize: 9, // A4
        orientation: 'portrait',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        margins: {
          left: 0.7,
          right: 0.7,
          top: 0.75,
          bottom: 0.75,
          header: 0.3,
          footer: 0.3
        }
      }
    });
    
    // Sütun genişliklerini ayarla
    worksheet.columns = [
      { header: '', key: 'a', width: 30 },  // A sütunu
      { header: '', key: 'b', width: 20 },  // B sütunu
      { header: '', key: 'c', width: 20 },  // C sütunu
      { header: '', key: 'd', width: 20 },  // D sütunu
    ];
    
    // Stil tanımlamaları
    const styles = {
      title: {
        font: { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC00000' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      sectionHeader: {
        font: { name: 'Arial', size: 12, bold: true },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDDDDDD' } },
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      tableHeader: {
        font: { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF808080' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      tableCell: {
        font: { name: 'Arial', size: 10 },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      label: {
        font: { name: 'Arial', size: 10, bold: true },
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      value: {
        font: { name: 'Arial', size: 10 },
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      cell: {
        font: { name: 'Arial', size: 11 },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      success: {
        font: { name: 'Arial', size: 11 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC6EFCE' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      warning: {
        font: { name: 'Arial', size: 11 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFEB9C' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      error: {
        font: { name: 'Arial', size: 11 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFC7CE' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      footer: {
        font: { name: 'Arial', size: 10, italic: true },
        alignment: { horizontal: 'center', vertical: 'middle' }
      }
    };

    // Logo (Base64 formatında Alas Fire logo) eklemek için (opsiyonel)
    // Projeye özel logo eklenebilir
    
    // Fallback to default Turkish text if t function is not provided
    const getTranslation = (key, fallback) => {
      return t ? t(key) : fallback;
    };
    
    // Determine locale for date formatting
    const isEnglish = t && (t('app.title') === 'Alas Design Calculator');
    const locale = isEnglish ? 'en-US' : 'tr-TR';
    
    // BAŞLIK
    const titleRow = worksheet.addRow([getTranslation('calculator.copyExport.excel_titles.calculation_report', 'Alas Fire Hesaplama Raporu')]);
    titleRow.height = 30;
    worksheet.mergeCells('A1:D1');
    applyStyleToRow(titleRow, styles.title);
    
    // PROJE BİLGİLERİ
    const projectHeaderRow = worksheet.addRow([getTranslation('calculator.copyExport.excel_titles.project_info', 'Proje Bilgileri')]);
    projectHeaderRow.height = 24;
    worksheet.mergeCells('A2:D2');
    applyStyleToRow(projectHeaderRow, styles.sectionHeader);
    
    // Proje detayları
    addLabelValueRow(worksheet, getTranslation('calculator.copyExport.excel_titles.project_name', 'Proje Adı'), projectName, styles);
    addLabelValueRow(worksheet, getTranslation('calculator.copyExport.excel_titles.room_name', 'Oda Adı'), roomName, styles);
    addLabelValueRow(worksheet, getTranslation('calculator.copyExport.excel_titles.date', 'Tarih'), new Date().toLocaleDateString(locale), styles);
    
    // ODA ÖZELLİKLERİ
    const roomHeaderRow = worksheet.addRow([getTranslation('calculator.copyExport.excel_titles.room_properties', 'Oda Özellikleri')]);
    roomHeaderRow.height = 24;
    worksheet.mergeCells(`A${roomHeaderRow.number}:D${roomHeaderRow.number}`);
    applyStyleToRow(roomHeaderRow, styles.sectionHeader);
    
    // Oda detayları
    addLabelValueRow(worksheet, getTranslation('calculator.copyExport.excel_titles.length', 'Uzunluk'), `${calculationResults.room.length || '-'} m`, styles);
    addLabelValueRow(worksheet, getTranslation('calculator.copyExport.excel_titles.width', 'Genişlik'), `${calculationResults.room.width || '-'} m`, styles);
    addLabelValueRow(worksheet, getTranslation('calculator.copyExport.excel_titles.height', 'Yükseklik'), `${calculationResults.room.height || '-'} m`, styles);
    addLabelValueRow(worksheet, getTranslation('calculator.copyExport.volume', 'Hacim'), `${calculationResults.room.volume.toFixed(2)} m³`, styles);
    addLabelValueRow(worksheet, getTranslation('calculator.copyExport.safety_factor', 'Emniyet Faktörü'), calculationResults.room.safety_factor.toFixed(2), styles);
    addLabelValueRow(worksheet, getTranslation('calculator.copyExport.adjusted_volume', 'Ayarlanmış Hacim'), `${calculationResults.room.adjusted_volume.toFixed(2)} m³`, styles);
    addLabelValueRow(worksheet, getTranslation('calculator.copyExport.fire_class', 'Yangın Sınıfı'), getFireClassName(calculationResults.room.fire_class, false, t), styles);
    addLabelValueRow(worksheet, getTranslation('calculator.copyExport.excel_titles.required_suppressant', 'Gerekli Söndürücü Miktarı'), `${calculationResults.room.required_mass.toFixed(2)} g`, styles);
    
    worksheet.addRow(['']); // Boş satır
    
    // SEÇİLİ ÜRÜNLER
    let selectedProducts = [];
    let selectionType = '';
    
    // Eğer uygulanmış bir seçim varsa, onu kullan
    if (calculationResults.appliedSelection) {
      selectionType = getAlgorithmName(calculationResults.appliedSelection.algorithm, t);
      selectedProducts = calculationResults.appliedSelection.results.selectedProducts || 
                        calculationResults.appliedSelection.results.products || [];
    } 
    // Yoksa, greedy algoritmasını kullan
    else if (calculationResults.selections && calculationResults.selections.greedy) {
      selectionType = getAlgorithmName('greedy', t);
      selectedProducts = calculationResults.selections.greedy.products;
    }
    
    // Seçili ürünler başlığı
    const productsHeaderRow = worksheet.addRow([getTranslation('calculator.copyExport.excel_titles.selected_products', 'Seçili Ürünler')]);
    productsHeaderRow.height = 24;
    worksheet.mergeCells(`A${productsHeaderRow.number}:D${productsHeaderRow.number}`);
    applyStyleToRow(productsHeaderRow, styles.sectionHeader);
    
    // Algoritma satırı kaldırıldı - artık Excel dosyalarında gösterilmiyor
    
    worksheet.addRow(['']); // Boş satır
    
    // Ürünler tablosu başlık
    const tableHeaderRow = worksheet.addRow([
      getTranslation('calculator.copyExport.excel_titles.product', 'Ürün'),
      getTranslation('calculator.copyExport.excel_titles.quantity', 'Adet'),
      getTranslation('calculator.copyExport.excel_titles.unit_effective_mass', 'Birim Etkin Kütle (g)'),
      getTranslation('calculator.copyExport.excel_titles.total_effective_mass', 'Toplam Etkin Kütle (g)')
    ]);
    tableHeaderRow.height = 20;
    applyStyleToRow(tableHeaderRow, styles.tableHeader);
    
    // Ürünleri ekle
    let totalCount = 0;
    let totalEffectiveMass = 0;
    
    selectedProducts.forEach(product => {
      const quantity = product.quantity || product.count || 0;
      const effectiveMass = product.effective_mass || 0;
      const totalMass = effectiveMass * quantity;
      
      const row = worksheet.addRow([
        getProductShortName(product, language),
        quantity,
        effectiveMass,
        totalMass.toFixed(2)
      ]);
      
      applyStyleToRow(row, styles.tableCell);
      
      totalCount += quantity;
      totalEffectiveMass += totalMass;
    });
    
    // Toplam satırı
    worksheet.addRow(['']); // Boş satır
    
    const totalRow = worksheet.addRow([
      getTranslation('calculator.copyExport.excel_titles.total', 'Toplam'), 
      totalCount, 
      '', 
      totalEffectiveMass.toFixed(2)
    ]);
    applyStyleToRow(totalRow, styles.tableHeader);
    
    worksheet.addRow(['']); // Boş satır
    
    // DOĞRULAMA BİLGİLERİ
    const verificationHeaderRow = worksheet.addRow([getTranslation('calculator.copyExport.excel_titles.suppression_capacity_control', 'Söndürme Kapasitesi Kontrolü')]);
    verificationHeaderRow.height = 24;
    worksheet.mergeCells(`A${verificationHeaderRow.number}:D${verificationHeaderRow.number}`);
    applyStyleToRow(verificationHeaderRow, styles.sectionHeader);
    
    // Doğrulama detayları
    if (calculationResults.selections) {
      let verificationRatio = 0;
      let fireClass = calculationResults.room.fire_class;
      let roomVolume = calculationResults.room.volume;
      
      if (calculationResults.appliedSelection) {
        verificationRatio = calculationResults.appliedSelection.results.verificationRatio;
      }
      else if (calculationResults.selections.greedy) {
        verificationRatio = calculationResults.selections.greedy.verificationRatio;
      }

      const row = worksheet.addRow([
        getTranslation('calculator.copyExport.excel_titles.verification_ratio', 'Doğrulama Oranı'), 
        `${verificationRatio.toFixed(2)}%`, 
        '', 
        ''
      ]);
      row.height = 20;
      applyStyleToRow(row, styles.tableCell);
      
      // Doğrulama oranına göre hücreyi renklendir
      if (verificationRatio >= 100) {
        row.getCell(2).style = styles.success;
      } else {
        row.getCell(2).style = styles.error;
      }
      
      // Alt bilgi ekle
      worksheet.addRow(['']); // Boş satır
      const footerRow = worksheet.addRow([getTranslation('calculator.copyExport.excel_titles.report_footer', 'Bu rapor Alas Fire Design Calculator tarafından oluşturulmuştur.')]);
      worksheet.mergeCells(`A${footerRow.number}:D${footerRow.number}`);
      applyStyleToRow(footerRow, styles.footer);
    }
    
    // Excel dosyasını kaydet
    const buffer = await workbook.xlsx.writeBuffer();
    const fileName = `${projectName}_${roomName}_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    saveExcelFile(buffer, fileName);

    return { success: true };
  } catch (error) {
    console.error('Excel dosyası oluşturulurken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Toplu hesaplamaları Excel dosyasına aktarır
 * @param {Array} calculations - Hesaplama sonuçları dizisi
 * @param {Function} t - Translation function for i18n support
 */
export const exportBulkCalculations = async (calculations, t = null, language = 'tr') => {
  try {
    // Translation helper function
    const getTranslation = (key, fallback) => {
      if (t && typeof t === 'function') {
        try {
          return t(key) || fallback;
        } catch (error) {
          return fallback;
        }
      }
      return fallback;
    };

    // Yeni bir çalışma kitabı oluştur
    const workbook = new ExcelJS.Workbook();
    
    // Çalışma kitabı özellikleri
    workbook.creator = 'Alas Fire Design Calculator';
    workbook.lastModifiedBy = 'Alas Fire';
    workbook.created = new Date();
    workbook.modified = new Date();
    
    // Çalışma sayfası ekle - Özet sayfası
    const summarySheet = workbook.addWorksheet(getTranslation('calculator.copyExport.excel_titles.summary', 'Özet'), {
      pageSetup: {
        paperSize: 9, // A4
        orientation: 'landscape',
        fitToPage: true
      }
    });
    
    // Stil tanımlamaları
    const styles = {
      title: {
        font: { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC00000' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      header: {
        font: { name: 'Arial', size: 12, bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF404040' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      tableHeader: {
        font: { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF808080' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      tableCell: {
        font: { name: 'Arial', size: 10 },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      label: {
        font: { name: 'Arial', size: 10, bold: true },
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      value: {
        font: { name: 'Arial', size: 10 },
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      cell: {
        font: { name: 'Arial', size: 11 },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      success: {
        font: { name: 'Arial', size: 11 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC6EFCE' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      warning: {
        font: { name: 'Arial', size: 11 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFEB9C' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      error: {
        font: { name: 'Arial', size: 11 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFC7CE' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      },
      footer: {
        font: { name: 'Arial', size: 10, italic: true },
        alignment: { horizontal: 'center', vertical: 'middle' }
      }
    };
    
    // Başlık satırı ekle
    const titleRow = summarySheet.addRow([getTranslation('calculator.copyExport.bulk_export.title', 'Alas Fire Toplu Hesaplama Raporu')]);
    titleRow.height = 30;
    summarySheet.mergeCells('A1:L1');
    applyStyleToCell(titleRow.getCell(1), styles.title);
    
    // Tarih satırı ekle - Get current language
    const currentLanguage = (t && t('language.code')) || 'tr'; // Get language code or default to tr
    const locale = currentLanguage === 'en' ? 'en-US' : 'tr-TR';
    const dateRow = summarySheet.addRow([`${getTranslation('calculator.copyExport.bulk_export.calculation_date', 'Hesaplama Tarihi')}: ${new Date().toLocaleString(locale)}`]);
    dateRow.height = 20;
    summarySheet.mergeCells('A2:L2');
    applyStyleToCell(dateRow.getCell(1), styles.header);
    
    // Boş satır ekle
    summarySheet.addRow(['']);
    
    // Sütun başlıklarını ekle
    const summaryHeaderRow = summarySheet.addRow([
      getTranslation('calculator.copyExport.bulk_export.row_number', 'Sıra'),
      getTranslation('calculator.copyExport.bulk_export.room_name', 'Oda Adı'),
      getTranslation('calculator.copyExport.bulk_export.project_name', 'Proje Adı'),
      getTranslation('calculator.copyExport.bulk_export.region', 'Bölge'),
      getTranslation('calculator.copyExport.bulk_export.company', 'Firma'),
      getTranslation('calculator.copyExport.bulk_export.dimensions', 'Boyutlar (m)'),
      getTranslation('calculator.copyExport.bulk_export.volume', 'Hacim (m³)'),
      getTranslation('calculator.copyExport.bulk_export.fire_class', 'Yangın Sınıfı'),
      getTranslation('calculator.copyExport.bulk_export.required_amount', 'Gerekli Miktar (g)'),
      getTranslation('calculator.copyExport.bulk_export.product_count', 'Ürün Adedi'),
      getTranslation('calculator.copyExport.bulk_export.verification_ratio', 'Doğrulama Oranı (%)'),
      getTranslation('calculator.copyExport.bulk_export.date', 'Tarih')
    ]);
    
    // Sütun genişliklerini ayarla - daha geniş yapıldı
    summarySheet.columns = [
      { width: 8 },   // Sıra
      { width: 25 },  // Oda Adı - genişletildi
      { width: 30 },  // Proje Adı - genişletildi
      { width: 20 },  // Bölge - genişletildi
      { width: 25 },  // Firma - genişletildi
      { width: 18 },  // Boyutlar - genişletildi
      { width: 15 },  // Hacim - genişletildi
      { width: 22 },  // Yangın Sınıfı - genişletildi
      { width: 18 },  // Gerekli Miktar - genişletildi
      { width: 15 },  // Ürün Adedi - genişletildi
      { width: 18 },  // Doğrulama Oranı - genişletildi
      { width: 22 }   // Tarih - genişletildi
    ];
    
    // Header stilini uygula
    summaryHeaderRow.eachCell((cell) => {
      applyStyleToCell(cell, styles.header);
    });
    
    // Her bir hesaplamayı ekle
    calculations.forEach((calculation, index) => {
      const room = calculation.room;
      const selection = calculation.selection;
      
      let totalCount = 0;
      let verificationRatio = 0;
      
      if (selection && selection.results) {
        verificationRatio = selection.results.verificationRatio || 0;
        
        // Ürün sayısını hesapla
        const products = selection.results.products || selection.results.selectedProducts || [];
        totalCount = products.filter(p => !p.isController).reduce((sum, p) => sum + (p.quantity || 0), 0);
      }
      
      const row = summarySheet.addRow([
        index + 1,
        room.room_name || 'İsimsiz Oda',
        calculation.project_name || room.project_name || 'İsimsiz Proje',
        calculation.region_name || '',
        calculation.company_name || '',
        `${room.length} × ${room.width} × ${room.height}`,
        `${formatNumber(room.volume)} m³`,
        getFireClassName(room.fire_class, true),
        `${formatNumber(room.required_mass)} g`,
        totalCount,
        `${formatNumber(verificationRatio)}%`,
        new Date(calculation.timestamp).toLocaleString('tr-TR')
      ]);
      
      applyStyleToRow(row, styles.cell);
      
      // Doğrulama oranına göre hücreyi renklendir
      if (verificationRatio >= 124) {
        row.getCell(11).style = styles.success;
      } else if (verificationRatio >= 100) {
        row.getCell(11).style = styles.warning;
      } else {
        row.getCell(11).style = styles.error;
      }
      
      // Her hesaplama için detay sayfası oluştur
      const sheetName = `${getTranslation('calculator.room', 'Oda')} ${index + 1}`;
      const detailSheet = workbook.addWorksheet(sheetName, {
        pageSetup: {
          paperSize: 9, // A4
          orientation: 'portrait',
          fitToPage: true,
          fitToWidth: 1,
          fitToHeight: 0,
          margins: {
            left: 0.7,
            right: 0.7,
            top: 0.75,
            bottom: 0.75,
            header: 0.3,
            footer: 0.3
          }
        }
      });
      
      // Detay sayfası sütunları
      detailSheet.columns = [
        { header: '', key: 'a', width: 30 },
        { header: '', key: 'b', width: 20 },
        { header: '', key: 'c', width: 20 },
        { header: '', key: 'd', width: 20 },
      ];
      
      // Başlık
      const detailTitleRow = detailSheet.addRow([`${getTranslation('calculator.room', 'Oda')} ${index + 1}: ${room.room_name || getTranslation('calculator.no_name_room', 'İsimsiz Oda')}`]);
      detailTitleRow.height = 30;
      detailSheet.mergeCells('A1:D1');
      detailTitleRow.getCell(1).style = styles.title;
      
              // Proje bilgileri
        const projectRow = detailSheet.addRow([getTranslation('calculator.copyExport.excel_titles.project_info', 'Proje Bilgileri')]);
        projectRow.height = 24;
        detailSheet.mergeCells('A2:D2');
        projectRow.getCell(1).style = styles.header;
        
        // Proje detayları
        addLabelValueRow(detailSheet, getTranslation('calculator.copyExport.excel_titles.project_name', 'Proje Adı'), calculation.project_name || room.project_name || getTranslation('calculator.no_name_project', 'İsimsiz Proje'), styles);
        addLabelValueRow(detailSheet, getTranslation('calculator.copyExport.excel_titles.room_name', 'Oda Adı'), room.room_name || getTranslation('calculator.no_name_room', 'İsimsiz Oda'), styles);
        if (calculation.region_name) {
          addLabelValueRow(detailSheet, getTranslation('calculator.copyExport.region', 'Bölge'), calculation.region_name, styles);
        }
        if (calculation.company_name) {
          addLabelValueRow(detailSheet, getTranslation('calculator.copyExport.company', 'Firma'), calculation.company_name, styles);
        }
        addLabelValueRow(detailSheet, getTranslation('calculator.copyExport.excel_titles.date', 'Tarih'), new Date(calculation.timestamp).toLocaleString(locale), styles);
      
              // Oda özellikleri
        const roomHeaderRow = detailSheet.addRow([getTranslation('calculator.copyExport.excel_titles.room_properties', 'Oda Özellikleri')]);
        roomHeaderRow.height = 24;
        detailSheet.mergeCells(`A${roomHeaderRow.number}:D${roomHeaderRow.number}`);
        roomHeaderRow.getCell(1).style = styles.header;
      
      // Oda detayları
      addLabelValueRow(detailSheet, getTranslation('calculator.copyExport.excel_titles.length', 'Uzunluk'), `${room.length || '-'} m`, styles);
      addLabelValueRow(detailSheet, getTranslation('calculator.copyExport.excel_titles.width', 'Genişlik'), `${room.width || '-'} m`, styles);
      addLabelValueRow(detailSheet, getTranslation('calculator.copyExport.excel_titles.height', 'Yükseklik'), `${room.height || '-'} m`, styles);
      addLabelValueRow(detailSheet, getTranslation('calculator.copyExport.volume', 'Hacim'), `${formatNumber(room.volume)} m³`, styles);
      addLabelValueRow(detailSheet, getTranslation('calculator.copyExport.safety_factor', 'Emniyet Faktörü'), formatNumber(room.safety_factor), styles);
      addLabelValueRow(detailSheet, getTranslation('calculator.copyExport.adjusted_volume', 'Ayarlanmış Hacim'), `${formatNumber(room.adjusted_volume)} m³`, styles);
      addLabelValueRow(detailSheet, getTranslation('calculator.copyExport.fire_class', 'Yangın Sınıfı'), getFireClassName(room.fire_class, false, t), styles);
      addLabelValueRow(detailSheet, getTranslation('calculator.copyExport.excel_titles.required_suppressant', 'Gerekli Söndürücü Miktarı'), `${formatNumber(room.required_mass)} g`, styles);
      
      detailSheet.addRow(['']);
      
      // Seçili ürünler
      if (selection && selection.results) {
        const productsHeaderRow = detailSheet.addRow([getTranslation('calculator.copyExport.excel_titles.selected_products', 'Seçili Ürünler')]);
        productsHeaderRow.height = 24;
        detailSheet.mergeCells(`A${productsHeaderRow.number}:D${productsHeaderRow.number}`);
        productsHeaderRow.getCell(1).style = styles.header;
        
            // Algoritma satırı kaldırıldı - artık Excel dosyalarında gösterilmiyor
        
        detailSheet.addRow(['']);
        
        // Ürünler tablosu başlık
        const tableHeaderRow = detailSheet.addRow([
          getTranslation('calculator.copyExport.excel_titles.product', 'Ürün'), 
          getTranslation('calculator.copyExport.excel_titles.quantity', 'Adet'), 
          getTranslation('calculator.copyExport.excel_titles.unit_effective_mass', 'Birim Etkin Kütle (g)'), 
          getTranslation('calculator.copyExport.excel_titles.total_effective_mass', 'Toplam Etkin Kütle (g)')
        ]);
        tableHeaderRow.height = 20;
        applyStyleToRow(tableHeaderRow, styles.tableHeader);
        
        // Ürünleri ekle
        let totalCount = 0;
        let totalEffectiveMass = 0;
        
        const products = selection.results.products || selection.results.selectedProducts || [];
        
        products.filter(p => !p.isController).forEach(product => {
          const quantity = product.quantity || product.count || 0;
          const effectiveMass = product.effective_mass || 0;
          const totalMass = effectiveMass * quantity;
          
          const row = detailSheet.addRow([
            getProductShortName(product, language),
            quantity,
            effectiveMass,
            formatNumber(totalMass)
          ]);
          
          applyStyleToRow(row, styles.tableCell);
          
          totalCount += quantity;
          totalEffectiveMass += totalMass;
        });
        
        // Toplam satırı
        detailSheet.addRow(['']);
        
        const totalRow = detailSheet.addRow([getTranslation('calculator.copyExport.excel_titles.total', 'Toplam'), totalCount, '', formatNumber(totalEffectiveMass)]);
        applyStyleToRow(totalRow, styles.tableHeader);
        
        detailSheet.addRow(['']);
        
        // Doğrulama bilgileri
        const verificationHeaderRow = detailSheet.addRow([getTranslation('calculator.copyExport.excel_titles.suppression_capacity_control', 'Söndürme Kapasitesi Kontrolü')]);
        verificationHeaderRow.height = 24;
        detailSheet.mergeCells(`A${verificationHeaderRow.number}:D${verificationHeaderRow.number}`);
        verificationHeaderRow.getCell(1).style = styles.header;
        
        // Doğrulama oranı
        const verificationRow = detailSheet.addRow([getTranslation('calculator.copyExport.excel_titles.verification_ratio', 'Doğrulama Oranı'), `${formatNumber(verificationRatio)}%`, '', '']);
        verificationRow.height = 20;
        applyStyleToRow(verificationRow, styles.tableCell);
        
        // Doğrulama oranına göre hücreyi renklendir
        if (verificationRatio >= 124) {
          verificationRow.getCell(2).style = styles.success;
        } else if (verificationRatio >= 100) {
          verificationRow.getCell(2).style = styles.warning;
        } else {
          verificationRow.getCell(2).style = styles.error;
        }
        
        // Doğrulama açıklaması
        let verificationExplanation = '';
        if (verificationRatio >= 124) {
          verificationExplanation = getTranslation('calculator.notifications.verification_ratio_optimal', 'Optimal doğrulama oranı (124% ve üzeri)');
        } else if (verificationRatio >= 100) {
          verificationExplanation = getTranslation('calculator.notifications.verification_ratio_sufficient', 'Yeterli doğrulama oranı (100% - 124%)');
        } else {
          verificationExplanation = getTranslation('calculator.notifications.verification_ratio_insufficient', 'Yetersiz doğrulama oranı (100% altı)');
        }
        
        const explanationRow = detailSheet.addRow([getTranslation('calculator.status', 'Durum'), verificationExplanation, '', '']);
        explanationRow.height = 20;
        applyStyleToRow(explanationRow, styles.tableCell);
        
        if (verificationRatio >= 124) {
          explanationRow.getCell(2).style = styles.success;
        } else if (verificationRatio >= 100) {
          explanationRow.getCell(2).style = styles.warning;
        } else {
          explanationRow.getCell(2).style = styles.error;
        }
      }
      
      // Alt bilgi
      detailSheet.addRow(['']);
      const footerRow = detailSheet.addRow([getTranslation('calculator.copyExport.excel_titles.report_footer', 'Bu rapor Alas Fire Design Calculator tarafından oluşturulmuştur.')]);
      detailSheet.mergeCells(`A${footerRow.number}:D${footerRow.number}`);
      footerRow.getCell(1).style = styles.footer;
    });
    
    // Özet sayfasına alt bilgi ekle
    summarySheet.addRow(['']);
    summarySheet.addRow(['']);
    const summaryFooterRow = summarySheet.addRow([getTranslation('calculator.copyExport.excel_titles.report_footer', 'Bu rapor Alas Fire Design Calculator tarafından oluşturulmuştur.')]);
    summarySheet.mergeCells(`A${summaryFooterRow.number}:M${summaryFooterRow.number}`);
    summaryFooterRow.getCell(1).style = styles.footer;
    
    // Excel dosyasını kaydet
    const buffer = await workbook.xlsx.writeBuffer();
    const fileNamePrefix = getTranslation('calculator.bulk_export_filename', 'Alas_Fire_Toplu_Rapor');
    const fileName = `${fileNamePrefix}_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    saveExcelFile(buffer, fileName);
    
    return { success: true };
  } catch (error) {
    console.error('Toplu Excel raporu oluşturulurken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Excel dosyasını kaydet
 * @param {Buffer} buffer - Excel dosyası buffer'ı
 * @param {String} fileName - Dosya adı
 */
const saveExcelFile = (buffer, fileName) => {
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, fileName);
};

/**
 * Satıra stil uygula
 * @param {ExcelJS.Row} row - Satır
 * @param {Object} style - Uygulanacak stil
 */
const applyStyleToRow = (row, style) => {
  row.eachCell(cell => {
    applyStyleToCell(cell, style);
  });
};

/**
 * Hücreye stil uygula
 * @param {ExcelJS.Cell} cell - Hücre
 * @param {Object} style - Uygulanacak stil
 */
const applyStyleToCell = (cell, style) => {
  if (style.font) cell.font = style.font;
  if (style.fill) cell.fill = style.fill;
  if (style.alignment) cell.alignment = style.alignment;
  if (style.border) cell.border = style.border;
};

/**
 * Etiket-Değer satırı ekle
 * @param {ExcelJS.Worksheet} worksheet - Çalışma sayfası
 * @param {String} label - Etiket
 * @param {String} value - Değer
 * @param {Object} styles - Stiller
 */
const addLabelValueRow = (worksheet, label, value, styles) => {
  const row = worksheet.addRow([label, value, '', '']);
  worksheet.mergeCells(`B${row.number}:D${row.number}`);
  
  applyStyleToCell(row.getCell(1), styles.label);
  applyStyleToCell(row.getCell(2), styles.value);
};

/**
 * Yangın sınıfı adını döndürür
 * @param {String} fireClass - Yangın sınıfı kodu
 * @param {Boolean} shortVersion - Kısa sürüm (true) veya uzun sürüm (false)
 * @param {Function} t - Translation function for i18n support
 * @returns {String} - Yangın sınıfı açıklaması
 */
const getFireClassName = (fireClass, shortVersion = false, t = null) => {
  if (t) {
    // İngilizce mi Türkçe mi olduğunu kontrol et
    const isEnglish = t('app.title') === 'Alas Design Calculator';
    
    // Dil İngilizce ise, güncellenmiş İngilizce adları kullan
    if (isEnglish) {
      switch (fireClass) {
        case 'wooden-stack':
          return shortVersion ? 'Wood Crib' : 'Wood Crib Fire Test';
        case 'a-wooden-stack':
          return shortVersion ? 'Class A Wood Crib' : 'Class A Compatible Wood Crib Test';
        case 'a-polymer-sheet':
          return shortVersion ? 'Polymeric Sheet' : 'Polymeric Sheet Fire Test';
        case 'b-nheptane-liquid':
          return shortVersion ? 'N-Heptane' : 'N-Heptane Pan Fire Test';
        case 'wooden-stack-general':
          return shortVersion ? 'Wood (General)' : 'General Wood Crib';
        default:
          return fireClass;
      }
    }
    
    // Türkçe için t fonksiyonu ile çeviri kullan
    switch (fireClass) {
      case 'a-wooden-stack':
        return shortVersion ? t('calculator.fire_class_a_short') : t('calculator.fire_class_a');
      case 'b-nheptane-liquid':
        return shortVersion ? t('calculator.fire_class_b_short') : t('calculator.fire_class_b');
      case 'a-polymer-sheet':
        return shortVersion ? t('calculator.fire_class_a_polymer_short') : t('calculator.fire_class_a_polymer');
      case 'wooden-stack':
        return shortVersion ? t('calculator.fire_class_wooden_short') : t('calculator.fire_class_wooden_general');
      case 'wooden-stack-general':
        return shortVersion ? t('calculator.fire_class_wooden_short') : t('calculator.fire_class_wooden_general');
      default:
        return fireClass;
    }
  }
  
  // Fallback to Turkish
  switch (fireClass) {
    case 'a-wooden-stack':
      return shortVersion ? 'A Sınıfı Ahşap' : 'A Sınıfı Ahşap Deste';
    case 'b-nheptane-liquid':
      return shortVersion ? 'B Sınıfı N-Heptan' : 'B Sınıfı N-Heptan Sıvı';
    case 'a-polymer-sheet':
      return shortVersion ? 'A Sınıfı Polimer' : 'A Sınıfı Polimer Levha';
    case 'wooden-stack':
      return shortVersion ? 'Ahşap (Genel)' : 'Ahşap Deste';
    case 'wooden-stack-general':
      return shortVersion ? 'Ahşap (Genel)' : 'Genel Ahşap Deste';
    default:
      return fireClass;
  }
};

/**
 * Algoritma adını döndürür
 * @param {String} algorithm - Algoritma kodu
 * @param {Function} t - Translation function for i18n support
 * @returns {String} - Algoritma açıklaması
 */
const getAlgorithmName = (algorithm, t = null) => {
  if (t) {
    // For Excel exports, we don't show algorithm name anymore
    return '';
  }
  
  // Fallback to Turkish
  switch (algorithm) {
    case 'greedy':
      return 'Excel';
    case 'hybrid':
      return 'Hibrit (Orta seviye ürünler)';
    case 'balanced':
      return 'Dengeli (Küçük ürün ağırlıklı)';
    case 'manual':
      return 'Manuel Seçim';
    default:
      return algorithm;
  }
}; 